/** Read-only playbook for an officer's team member — no dashboard access needed. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Copy, ExternalLink, ArrowRight, Check } from "lucide-react";
import { toast, Toaster } from "sonner";
import { ASSETS } from "@/lib/constants";
import { copyText } from "@/lib/clipboard";
import { PlaybookSteps } from "@/components/dashboard/RecruitPlaybookCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlaybookShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/playbook/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This playbook link is no longer valid."));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="playbook-share-error">
          <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-10 w-auto mx-auto mb-8" />
          <p className="text-sm text-ink-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  const { officer, links, division, steps } = data;

  return (
    <div className="min-h-screen bg-linen antialiased">
      <header className="bg-navy-deep">
        <div className="container-grc py-12 sm:py-16 max-w-3xl">
          <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
          <p className="mt-8 text-[11px] uppercase tracking-[0.2em] text-[#A6D24A]">
            Recruiting playbook · {officer.name}
          </p>
          <h1 className="mt-3 font-heading font-medium text-4xl sm:text-5xl text-white leading-[1.08]">
            How to sign up<br />a partner.
          </h1>
          <p className="mt-5 text-base text-white/80 leading-relaxed max-w-xl">
            Six steps, in order. Your side is the ask and the relationship — Great Rate Check handles the
            application, the agreement, approval, and the partner's first week.
          </p>
        </div>
      </header>

      <main className="container-grc py-12 sm:py-16 max-w-3xl">
        <div className="rounded-2xl bg-navy p-5 sm:p-6" data-testid="playbook-share-link">
          <p className="text-[11px] uppercase tracking-widest text-lime font-bold">
            {officer.name.split(" ")[0]}'s partner application link
          </p>
          <p className="mt-2 font-mono text-[12.5px] text-white/90 break-all">{links.apply_link}</p>
          <p className="mt-2.5 text-[12.5px] text-white/60 leading-relaxed">
            Always send this one. Applications through this link are credited to {officer.name.split(" ")[0]}.
          </p>
          <button
            onClick={async () => {
              const ok = await copyText(links.apply_link);
              toast[ok ? "success" : "error"](ok ? "Application link copied" : "Couldn't copy that");
            }}
            data-testid="playbook-share-copy"
            className="mt-3.5 inline-flex items-center gap-1.5 rounded-full bg-lime text-navy-deep text-xs font-bold px-4 py-2 hover:brightness-105 transition-all"
          >
            <Copy size={13} /> Copy the link
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-teal/30 bg-teal-light/20 p-4">
            <p className="text-[11px] uppercase tracking-widest text-teal font-bold">Your side</p>
            <ul className="mt-2.5 space-y-1.5">
              {division.officer.map((l) => (
                <li key={l} className="flex gap-2 text-[13px] text-ink-700 leading-relaxed">
                  <ArrowRight size={13} className="shrink-0 mt-1 text-teal" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-linen-border bg-white p-4">
            <p className="text-[11px] uppercase tracking-widest text-ink-500 font-bold">Great Rate Check's side</p>
            <ul className="mt-2.5 space-y-1.5">
              {division.grc.map((l) => (
                <li key={l} className="flex gap-2 text-[13px] text-ink-600 leading-relaxed">
                  <Check size={13} className="shrink-0 mt-1 text-ink-400" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <PlaybookSteps steps={steps} readOnly />

        <a
          href={links.pitch_link}
          target="_blank"
          rel="noreferrer"
          data-testid="playbook-share-pitch"
          className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-teal transition-colors"
        >
          Read the partner pitch page <ExternalLink size={14} />
        </a>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
